import { Icon, Tooltip } from '@design-system';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useRef } from 'react';

import { ProductV2Storyblok } from '../../../types/generated-storyblok';
import { ProductInfoLabel } from './ProductInfoLabel';

type ProductEditionSizeProps = {
  editionNote?: string;
  editionSize?: string | number;
  editionFormat?: ProductV2Storyblok['editionFormat'];
  className?: string;
};

const editionFormatLabelKey: Record<
  Exclude<ProductV2Storyblok['editionFormat'], ''>,
  string
> = {
  'limited': 'productDetails.editionFormat.limited',
  'timed': 'productDetails.editionFormat.timed',
  'open': 'productDetails.editionFormat.open',
  'unique': 'productDetails.editionFormat.unique',
  'series': 'productDetails.editionFormat.series',
};

export const ProductEditionSize = ({
  className,
  editionFormat,
  editionNote,
  editionSize,
}: ProductEditionSizeProps) => {
  const { t } = useTranslation('product');
  const infoIconRef = useRef<HTMLButtonElement>(null);

  let amount = 0;
  if (typeof editionSize === 'number') {
    amount = editionSize;
  } else if (editionSize) {
    amount = parseInt(editionSize, 10);
  }

  const showTimedTooltip = editionFormat === 'timed' && amount === 0;

  const label = (() => {
    switch (editionFormat) {
      case 'limited':
        return amount > 0
          ? t('productDetails.editionSize.limited', { amount })
          : t(editionFormatLabelKey.limited);
      case 'series':
        return amount > 0
          ? t('productDetails.editionSize.series', { amount })
          : t(editionFormatLabelKey.series);
      case 'timed':
        return amount > 0
          ? t('productDetails.editionSize.limited', { amount })
          : t(editionFormatLabelKey.timed);
      case 'open':
        return t(editionFormatLabelKey.open);
      case 'unique':
        return t(editionFormatLabelKey.unique);
      default:
        return amount > 0 ? t('editionSize', { amount }) : editionNote;
    }
  })();

  if (!label) return null;

  return (
    <span
      className={classNames(
        'inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1',
        className,
      )}
    >
      <ProductInfoLabel
        trailing={
          showTimedTooltip ? (
            <>
              <button
                ref={infoIconRef}
                aria-label={t('productDetails.editionSize.timed')}
                className="inline-flex shrink-0"
                type="button"
              >
                <Icon name="ic_info" size="xs" />
              </button>
              <Tooltip parentRef={infoIconRef} selectionRef={infoIconRef}>
                {t('productDetails.editionSize.timed')}
              </Tooltip>
            </>
          ) : null
        }
      >
        {label}
      </ProductInfoLabel>
    </span>
  );
};
